import { useEffect, useState, useCallback } from 'react'
import { Star, Folder, Trash2, Download } from 'lucide-react'
import { bookmarkApi, type Bookmark, type BookmarkFolder } from '../api/bookmarks'
import { articleApi, type ArticleExtras } from '../api/search'
import { useBookmarks } from '../contexts/BookmarkContext'

function articleUrl(pmcid: string): string {
  if (pmcid.startsWith('PMID_')) return `https://pubmed.ncbi.nlm.nih.gov/${pmcid.replace('PMID_', '')}/`
  return `https://www.ncbi.nlm.nih.gov/pmc/articles/${pmcid}`
}
function articleLabel(pmcid: string): string {
  return pmcid.startsWith('PMID_') ? `PMID:${pmcid.replace('PMID_', '')}` : pmcid
}

export default function BookmarksPage() {
  const { folders, totalCount, refresh } = useBookmarks()
  const [selected, setSelected] = useState<string>('all')  // 'all' | 'unsorted' | folderId
  const [bookmarks, setBookmarks] = useState<Bookmark[]>([])
  const [loading, setLoading] = useState(false)
  const [newFolder, setNewFolder] = useState('')
  const [expanded, setExpanded] = useState<string | null>(null)
  const [extras, setExtras] = useState<Map<string, ArticleExtras | null>>(new Map())

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const folderArg = selected === 'all' ? undefined : selected
      setBookmarks(await bookmarkApi.listBookmarks(folderArg))
    } catch {
      setBookmarks([])
    } finally {
      setLoading(false)
    }
  }, [selected])

  useEffect(() => { load() }, [load])

  const foldersSum = folders.reduce((s, f) => s + (f.bookmark_count || 0), 0)
  const unsortedCount = Math.max(0, totalCount - foldersSum)

  const folderName = (id?: string | null) =>
    id ? (folders.find(f => f.folder_id === id)?.name || '') : 'Unsorted'

  const toggleExpand = async (pmcid: string) => {
    if (expanded === pmcid) { setExpanded(null); return }
    setExpanded(pmcid)
    if (!extras.has(pmcid)) {
      try {
        const res = await articleApi.getExtras(pmcid)
        setExtras(prev => new Map(prev).set(pmcid, res.data))
      } catch {
        setExtras(prev => new Map(prev).set(pmcid, null))
      }
    }
  }

  const removeOne = async (pmcid: string) => {
    await bookmarkApi.removeBookmark(pmcid)
    setBookmarks(prev => prev.filter(b => b.pmcid !== pmcid))
    refresh()
  }

  const moveOne = async (pmcid: string, folderId: string | null) => {
    await bookmarkApi.moveBookmark(pmcid, folderId)
    refresh()
    load()
  }

  const addFolder = async () => {
    const name = newFolder.trim()
    if (!name) return
    try {
      await bookmarkApi.createFolder({ name })
      setNewFolder('')
      refresh()
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Failed to create folder')
    }
  }

  const deleteFolder = async (f: BookmarkFolder) => {
    if (!confirm(`Delete folder "${f.name}"? Its bookmarks become Unsorted (not deleted).`)) return
    await bookmarkApi.deleteFolder(f.folder_id)
    if (selected === f.folder_id) setSelected('all')
    refresh()
    load()
  }

  const exportCsv = () => {
    // Export only the currently selected view (All / Unsorted / a folder).
    if (bookmarks.length === 0) { alert('No bookmarks to export in this view.'); return }
    const esc = (v: string) => `"${(v || '').replace(/"/g, '""')}"`
    const header = ['pmcid', 'title', 'folder', 'url', 'created_at']
    const lines = [header.join(',')]
    for (const b of bookmarks) {
      lines.push([
        esc(articleLabel(b.pmcid)),
        esc(b.title || ''),
        esc(folderName(b.folder_id)),
        esc(articleUrl(b.pmcid)),
        esc(b.created_at),
      ].join(','))
    }
    const slug = selected === 'all' ? 'all'
      : selected === 'unsorted' ? 'unsorted'
      : (folderName(selected) || 'folder').replace(/[^a-z0-9]+/gi, '-').toLowerCase()
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `bookmarks_${slug}_${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  const FolderButton = ({ id, label, count }: { id: string; label: string; count?: number }) => (
    <button
      onClick={() => setSelected(id)}
      className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-left ${
        selected === id ? 'bg-blue-50 text-blue-700 font-medium' : 'hover:bg-gray-100 text-gray-700'
      }`}
    >
      <Folder className="w-4 h-4 text-gray-400" />
      <span className="flex-1 truncate">{label}</span>
      {typeof count === 'number' && <span className="text-xs text-gray-400">{count}</span>}
    </button>
  )

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Star className="w-6 h-6 text-yellow-500" fill="currentColor" /> Bookmarks
        </h1>
        <button
          onClick={exportCsv}
          title="Export the bookmarks in the selected folder/view to CSV"
          className="inline-flex items-center gap-2 px-3 py-2 bg-gray-600 text-white rounded-lg text-sm font-medium hover:bg-gray-700"
        >
          <Download className="w-4 h-4" /> Export CSV
        </button>
      </div>
      <div className="flex gap-6">
        {/* Folder sidebar */}
        <div className="w-64 shrink-0 space-y-1">
          <FolderButton id="all" label="All bookmarks" count={totalCount} />
          <FolderButton id="unsorted" label="Unsorted" count={unsortedCount} />
          <div className="pt-2 mt-2 border-t">
            {folders.map(f => (
              <div key={f.folder_id} className="group flex items-center">
                <div className="flex-1"><FolderButton id={f.folder_id} label={f.name} count={f.bookmark_count} /></div>
                <button
                  onClick={() => deleteFolder(f)}
                  className="p-1 text-gray-300 hover:text-red-600 opacity-0 group-hover:opacity-100"
                  title="Delete folder"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
          <div className="flex gap-1 pt-2">
            <input
              type="text"
              value={newFolder}
              onChange={e => setNewFolder(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') addFolder() }}
              placeholder="New folder…"
              className="flex-1 px-2 py-1.5 border rounded text-sm"
            />
            <button onClick={addFolder} disabled={!newFolder.trim()} className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm disabled:opacity-50">Add</button>
          </div>
        </div>

        {/* Bookmark list */}
        <div className="flex-1 bg-white rounded-xl border shadow-sm overflow-hidden">
          {loading ? (
            <div className="p-6 text-sm text-gray-400">Loading…</div>
          ) : bookmarks.length === 0 ? (
            <div className="p-8 text-center text-gray-400 text-sm">No bookmarks here yet. Click the ★ next to an article in search results or AI analysis.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Article</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Title</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Folder</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {bookmarks.map(b => {
                  const isOpen = expanded === b.pmcid
                  const ex = extras.get(b.pmcid)
                  return (
                    <tr key={b.bookmark_id} className="hover:bg-gray-50 align-top cursor-pointer" onClick={() => toggleExpand(b.pmcid)}>
                      <td className="px-4 py-2 whitespace-nowrap">
                        <span className="text-gray-400 mr-1">{isOpen ? '▾' : '▸'}</span>
                        <a href={articleUrl(b.pmcid)} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="text-blue-600 hover:underline font-mono">{articleLabel(b.pmcid)}</a>
                      </td>
                      <td className="px-4 py-2">
                        <div className="max-w-md break-words">{b.title || <span className="text-gray-400">—</span>}</div>
                        {isOpen && (
                          <div className="mt-2 text-xs text-gray-600 space-y-1 font-normal" onClick={e => e.stopPropagation()}>
                            {ex === undefined ? (
                              <div className="text-gray-400">Loading details…</div>
                            ) : ex === null ? (
                              <div className="text-gray-400">No additional details available.</div>
                            ) : (
                              <>
                                {ex.authors && <div><span className="font-medium text-gray-700">Authors:</span> {ex.authors}</div>}
                                {ex.journal && <div><span className="font-medium text-gray-700">Journal:</span> {ex.journal}</div>}
                                {ex.abstract && <div><span className="font-medium text-gray-700">Abstract:</span> {ex.abstract}</div>}
                                {!ex.authors && !ex.journal && !ex.abstract && <div className="text-gray-400">No additional details available.</div>}
                              </>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-2" onClick={e => e.stopPropagation()}>
                        <select
                          value={b.folder_id || ''}
                          onChange={e => moveOne(b.pmcid, e.target.value || null)}
                          className="text-xs border rounded px-2 py-1"
                        >
                          <option value="">Unsorted</option>
                          {folders.map(f => <option key={f.folder_id} value={f.folder_id}>{f.name}</option>)}
                        </select>
                      </td>
                      <td className="px-4 py-2 text-right" onClick={e => e.stopPropagation()}>
                        <button onClick={() => removeOne(b.pmcid)} className="text-gray-300 hover:text-red-600" title="Remove bookmark">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  )
}
